import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { ExternalLink, LogIn, Play } from "lucide-react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { DataAdapter } from "@shared-core/data/adapter";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import type {
  ScenarioRecipe,
  ScenarioRun,
  ScenarioRunDetail,
  ScenarioRunSupplier,
} from "@shared-core/data/journey-testing-types";
import { madeRecordsOf } from "@shared-core/domain/journey-testing-run-links";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import { formatPreviewTime } from "@shared-core/domain/preview-clock";
import { Badge } from "../../app/components/primitives/badge";
import { Button } from "../../app/components/primitives/button";
import { Label } from "../../app/components/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../app/components/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { ListPagination } from "../../app/components/patterns/ListPagination";
import { SlideOverPanel } from "../../app/components/patterns/SlideOverPanel";
import {
  EmptyRow,
  FailedRow,
  LoadingRow,
  describeError,
  useLoaded,
} from "./consoleState";

const STATUS_TONE: Record<ScenarioRun["status"], string> = {
  running: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300",
  done: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
  failed: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300",
};

/** Scenarios (07 §1): pick a recipe and a stage to stop at, run it, and open what it made. */
export function ScenariosTab() {
  const adapter = useDataAdapter();
  const [page, setPage] = useState(1);
  const loadRecipes = useCallback(
    () => adapter.listScenarioRecipes(),
    [adapter],
  );
  const loadRuns = useCallback(
    () => adapter.listScenarioRuns({ page, pageSize: 20 }),
    [adapter, page],
  );
  const [recipes, reloadRecipes] = useLoaded(loadRecipes);
  const [runs, reloadRuns] = useLoaded(loadRuns);
  const [openRunId, setOpenRunId] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      {recipes.status === "loaded" && (
        <RecipePicker
          recipes={recipes.data}
          onRan={(run) => {
            reloadRuns();
            setOpenRunId(run.id);
          }}
        />
      )}
      {recipes.status === "failed" && (
        <FailedRow message={recipes.message} onRetry={reloadRecipes} />
      )}

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Runs</h2>
        {runs.status === "loading" && (
          <LoadingRow label="Loading scenario runs…" />
        )}
        {runs.status === "failed" && (
          <FailedRow message={runs.message} onRetry={reloadRuns} />
        )}
        {runs.status === "loaded" && runs.data.total === 0 && (
          <EmptyRow>No scenarios yet — run one.</EmptyRow>
        )}
        {runs.status === "loaded" && runs.data.total > 0 && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Recipe</TableHead>
                  <TableHead>Stopped at</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created by</TableHead>
                  <TableHead>At (preview time)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.data.items.map((run) => (
                  <TableRow
                    key={run.id}
                    className="cursor-pointer"
                    onClick={() => setOpenRunId(run.id)}
                  >
                    <TableCell className="font-medium">{run.recipe}</TableCell>
                    <TableCell>{run.stop_at}</TableCell>
                    <TableCell>
                      <Badge className={STATUS_TONE[run.status]}>
                        {run.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{run.created_by}</TableCell>
                    <TableCell>{formatPreviewTime(run.started_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <ListPagination
              page={runs.data.page}
              totalPages={runs.data.totalPages}
              onPageChange={setPage}
            />
          </>
        )}
      </section>

      <ScenarioRunPanel runId={openRunId} onClose={() => setOpenRunId(null)} />
    </div>
  );
}

function RecipePicker({
  recipes,
  onRan,
}: {
  recipes: ScenarioRecipe[];
  onRan: (run: ScenarioRunDetail) => void;
}) {
  const adapter = useDataAdapter();
  const [recipeName, setRecipeName] = useState(recipes[0]?.name ?? "");
  const recipe = recipes.find((r) => r.name === recipeName);
  const [stopAt, setStopAt] = useState<string>("");
  const [running, setRunning] = useState(false);

  useEffect(
    () => setStopAt(recipe?.stages[recipe.stages.length - 1] ?? ""),
    [recipe],
  );

  async function run() {
    if (!recipe) return;
    setRunning(true);
    try {
      const result = await adapter.runScenario({ recipe: recipe.name, stopAt });
      if (result.status === "failed") {
        toast.error(
          `${recipe.name} stopped at “${result.failed_step}”: ${result.error}`,
        );
      } else {
        toast.success(`${recipe.name} reached “${result.stop_at}”.`);
      }
      onRan(result);
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setRunning(false);
    }
  }

  return (
    <section className="grid gap-4 rounded-lg border p-4 md:grid-cols-[2fr_1fr_auto] md:items-end">
      <div className="grid gap-1.5">
        <Label htmlFor="scenario-recipe">Recipe</Label>
        <Select value={recipeName} onValueChange={setRecipeName}>
          <SelectTrigger id="scenario-recipe">
            <SelectValue placeholder="Choose a recipe" />
          </SelectTrigger>
          <SelectContent>
            {recipes.map((r) => (
              <SelectItem key={r.name} value={r.name}>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {recipe && (
          <p className="text-sm text-muted-foreground">{recipe.description}</p>
        )}
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="scenario-stop-at">Stop at</Label>
        <Select value={stopAt} onValueChange={setStopAt}>
          <SelectTrigger id="scenario-stop-at">
            <SelectValue placeholder="Stage" />
          </SelectTrigger>
          <SelectContent>
            {(recipe?.stages ?? []).map((stage) => (
              <SelectItem key={stage} value={stage}>
                {stage}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        onClick={() => void run()}
        disabled={!recipe || !stopAt || running}
      >
        <Play className="size-4" />
        {running ? "Running… (up to two minutes)" : "Run scenario"}
      </Button>
    </section>
  );
}

function ScenarioRunPanel({
  runId,
  onClose,
}: {
  runId: string | null;
  onClose: () => void;
}) {
  const adapter = useDataAdapter();
  const manifest = useObjectManifest();
  const capabilities = usePlatformCapabilities();
  const [run, setRun] = useState<ScenarioRunDetail | null>(null);
  const [signingIn, setSigningIn] = useState<string | null>(null);

  useEffect(() => {
    setRun(null);
    if (!runId) return;
    let cancelled = false;
    adapter
      .getScenarioRun(runId)
      .then((value) => !cancelled && setRun(value))
      .catch((err: unknown) => !cancelled && toast.error(describeError(err)));
    return () => {
      cancelled = true;
    };
  }, [adapter, runId]);

  const personId = run?.created.person_id;
  const made = run ? madeRecordsOf(manifest, run.created) : [];
  const madeHref = new Map(made.map((record) => [record.value, record.href]));
  const supplierSignIns = run ? run.suppliers.flatMap(supplierSignInsOf) : [];

  // Every sign-in is minted on click and never stored: the link works once, for 60 seconds.
  async function open(key: string, mint: () => Promise<{ url: string }>) {
    setSigningIn(key);
    try {
      const link = await mint();
      capabilities.openExternalUrl(link.url);
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setSigningIn(null);
    }
  }

  return (
    <SlideOverPanel
      open={runId !== null}
      onOpenChange={(open) => !open && onClose()}
      title={run ? `${run.recipe} → ${run.stop_at}` : "Scenario run"}
      description={
        run ? `${run.status} · ${formatPreviewTime(run.started_at)}` : undefined
      }
    >
      {!run ? (
        <LoadingRow label="Loading the run…" />
      ) : (
        <div className="space-y-6">
          {run.status === "failed" && (
            <p
              className="rounded-md bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950 dark:text-red-300"
              role="alert"
            >
              Stopped at “{run.failed_step}”: {run.error}
            </p>
          )}
          <section>
            <h3 className="mb-2 font-medium">Made</h3>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              {Object.entries(run.created).map(([key, value]) => (
                <div key={key} className="contents">
                  <dt className="text-muted-foreground">
                    {key.replace(/_/g, " ")}
                  </dt>
                  <dd className="font-mono">
                    {madeHref.get(value) ? (
                      <Link
                        className="text-primary underline"
                        to={madeHref.get(value) ?? ""}
                      >
                        {value}
                      </Link>
                    ) : (
                      value
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
          <section>
            <h3 className="mb-2 font-medium">Links</h3>
            <ul aria-label="Links" className="space-y-1 text-sm">
              {personId && (
                <li>
                  <SignInButton
                    label="Sign in as customer"
                    disabled={signingIn !== null}
                    onClick={() =>
                      void open("customer", () =>
                        adapter.createCustomerSignInLink(personId),
                      )
                    }
                  />
                </li>
              )}
              {supplierSignIns.map((signIn) => (
                <li key={signIn.key}>
                  <SignInButton
                    label={signIn.label}
                    disabled={signingIn !== null}
                    onClick={() =>
                      void open(signIn.key, () => signIn.mint(adapter))
                    }
                  />
                </li>
              ))}
              {made
                .filter((record) => record.href)
                .map((record) => (
                  <li key={record.key}>
                    <Link
                      className="text-primary underline"
                      to={record.href ?? ""}
                    >
                      {record.label}
                    </Link>
                  </li>
                ))}
              {run.links.map((link) => (
                <li key={`${link.kind}-${link.url}`}>
                  <a
                    className="inline-flex items-center gap-1 text-primary underline"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.label} <ExternalLink className="size-3" />
                  </a>
                </li>
              ))}
              {!personId &&
                supplierSignIns.length === 0 &&
                made.length === 0 &&
                run.links.length === 0 && (
                  <li className="text-muted-foreground">No links yet.</li>
                )}
            </ul>
          </section>
          <section>
            <h3 className="mb-2 font-medium">Steps</h3>
            <ol className="space-y-1 text-sm">
              {run.steps.map((step) => (
                <li key={step.index} className="flex gap-2">
                  <span
                    className={step.ok ? "text-emerald-600" : "text-red-600"}
                  >
                    {step.status_code}
                  </span>
                  <span>{step.name}</span>
                  <span className="truncate font-mono text-muted-foreground">
                    {step.method} {step.path}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        </div>
      )}
    </SlideOverPanel>
  );
}

/** A sign-in the tester mints on click and opens in a new tab: worded and drawn as a link, since it is one. */
function SignInButton({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 text-primary underline disabled:opacity-50"
      disabled={disabled}
      onClick={onClick}
    >
      <LogIn aria-hidden className="size-3.5" />
      {label}
      <ExternalLink aria-hidden className="size-3" />
    </button>
  );
}

/** One way into a supplier's portal for a run, minted on click. */
interface SupplierSignIn {
  key: string;
  label: string;
  mint: (adapter: DataAdapter) => Promise<{ url: string }>;
}

/**
 * PROVISIONAL (Ben, 21 Oct): a supplier with a portal user signs in as the first one; a supplier with none opens
 * each of the run's purchase orders as the supplier, by its order link.
 */
function supplierSignInsOf(supplier: ScenarioRunSupplier): SupplierSignIn[] {
  const person = supplier.portal_person;
  if (person) {
    return [
      {
        key: `supplier-${person.person_id}`,
        label: `Sign in as ${supplier.supplier_name}`,
        mint: (adapter) => adapter.createSupplierSignInLink(person.person_id),
      },
    ];
  }
  return supplier.purchase_order_nos.map((purchaseOrderNo) => ({
    key: `order-${purchaseOrderNo}`,
    label: `Open ${purchaseOrderNo} as ${supplier.supplier_name}`,
    mint: (adapter) => adapter.createSupplierOrderLink(purchaseOrderNo),
  }));
}
